import {
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Tooltip,
    Typography,
} from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import { useLocation, useNavigate } from 'react-router-dom';
import React from 'react';
import MessageIcon from '@mui/icons-material/Message';
import FestivalIcon from '@mui/icons-material/Festival';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import AssignmentTurnedInIcon from '@mui/icons-material/AssignmentTurnedIn';
import MailIcon from '@mui/icons-material/Mail';
import { useTranslation } from 'react-i18next';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HourglassTopRoundedIcon from '@mui/icons-material/HourglassTopRounded';
import TrendingUpRoundedIcon from '@mui/icons-material/TrendingUpRounded';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import { useCapabilities } from '../../hooks/useCapabilities';
import { CAPABILITIES } from '../../helpers/capabilities';
import { TIMEZONE } from '../../../config';
import { useIsCommissionGroupManager } from '../../hooks/useIsCommissionGroupManager';

const C = CAPABILITIES;

const timezoneShort =
    new Intl.DateTimeFormat('en-US', {
        timeZone: TIMEZONE,
        timeZoneName: 'short',
    })
        .formatToParts(new Date())
        .find((p) => p.type === 'timeZoneName')?.value || TIMEZONE;
const timezoneCity = TIMEZONE.split('/').pop()?.replace(/_/g, ' ') || TIMEZONE;
const timezoneLabel = `${timezoneShort} (${timezoneCity})`;

function SidebarMenuItem({ icon, title, open, selected, ...otherProps }) {
    const { t } = useTranslation();
    return (
        <ListItem disablePadding sx={{ display: 'block' }}>
            <Tooltip title={open ? '' : t(title)} placement="right">
                <ListItemButton
                    {...otherProps}
                    selected={selected}
                    sx={{
                        minHeight: 48,
                        px: 2.5,
                    }}
                >
                    <ListItemIcon
                        sx={{
                            minWidth: 0,
                            mr: 2,
                            justifyContent: 'center',
                        }}
                    >
                        {icon}
                    </ListItemIcon>
                    <ListItemText
                        primary={t(title)}
                        sx={{ opacity: open ? 1 : 0, transition: 'opacity 0.3s' }}
                    />
                </ListItemButton>
            </Tooltip>
        </ListItem>
    );
}

export default function SidebarMenu({ open, onNavigate }) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { can } = useCapabilities();
    const menuItems = [
        {
            title: 'venues.title',
            icon: <FestivalIcon />,
            path: '/venues',
            capability: [C.VENUES_VIEW, C.USERS_MANAGE, C.PAYMENT_METHODS_VIEW],
        },
        {
            title: 'shows.title',
            icon: <TheaterComedyIcon />,
            path: '/shows',
            capability: [
                C.SHOWS_VIEW,
                C.COUPONS_VIEW,
                C.INTEGRATIONS_MANAGE,
                C.COMMISSION_GROUPS_VIEW,
            ],
        },
        {
            title: 'bookings.title',
            icon: <ReceiptLongIcon />,
            path: '/bookings',
            capability: C.BOOKINGS_VIEW,
        },
        {
            title: 'crm.title',
            icon: <PeopleAltIcon />,
            path: '/crm',
            capability: C.BOOKINGS_VIEW,
        },
        {
            title: 'bookingitems.cateringview',
            icon: <AssignmentTurnedInIcon />,
            path: '/cateringview',
            capability: C.CATERING_VIEW,
        },
        {
            title: 'bookingitems.checkinview',
            icon: <FactCheckIcon />,
            path: '/checkinview',
            capability: C.CHECKIN_VIEW,
        },
        {
            title: 'manualrefunds.title',
            icon: <AccountBalanceWalletIcon />,
            path: '/manualrefunds',
            capability: C.BOOKINGS_REFUND,
        },
        {
            title: 'widgetWaitlist.waitlistLabel',
            icon: <HourglassTopRoundedIcon />,
            path: '/waitlist',
            capability: C.WAITLIST_MANAGE,
        },
        {
            title: 'upgrades.pageTitle',
            icon: <TrendingUpRoundedIcon />,
            path: '/upgrades',
            capability: C.UPGRADE_MANAGE,
        },
        {
            title: 'reports.title',
            icon: <AssessmentIcon />,
            path: '/reports',
            capability: [
                C.REPORTS_SALES_VIEW,
                C.REPORTS_TRANSACTIONS_VIEW,
                C.REPORTS_MANIFEST_VIEW,
                C.REPORTS_COMMISSION_VIEW,
                C.REPORTS_AFFILIATE_SALES_VIEW,
                C.REPORTS_AFFILIATE_INVOICE_VIEW,
            ],
        },
        {
            title: 'invoices.title',
            icon: <DescriptionIcon />,
            path: '/invoices',
            capability: C.INVOICES_VIEW,
        },
        {
            title: 'auditlogs.title',
            icon: <MessageIcon />,
            path: '/auditlogs',
            capability: C.LOGS_VIEW,
        },
        {
            title: 'emaillogs.title',
            icon: <MailIcon />,
            path: '/emaillogs',
            capability: C.LOGS_VIEW,
        },
    ];

    const { isSuperadmin, isCommissionGroupManager } = useIsCommissionGroupManager();

    return (
        <List style={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            {menuItems
                .filter((item) => can(item.capability))
                .map((item) => (
                    <SidebarMenuItem
                        key={item.path}
                        open={open}
                        title={item.title}
                        icon={item.icon}
                        selected={pathname === item.path || pathname.startsWith(`${item.path}/`)}
                        onClick={() => {
                            navigate(item.path);
                            onNavigate?.();
                        }}
                    />
                ))}

            {(isSuperadmin || isCommissionGroupManager) && (
                <SidebarMenuItem
                    key="/commission-group-management"
                    open={open}
                    title="commissionGroups.managementTitle"
                    icon={<GroupIcon />}
                    onClick={() => {
                        navigate('/commission-group-management');
                        onNavigate?.();
                    }}
                />
            )}
            <ListItem sx={{ mt: 'auto', display: 'block', px: 2.5, py: 1.5 }}>
                <Tooltip
                    title={open ? '' : t('common.allTimesIn', { timezone: timezoneLabel })}
                    placement="right"
                >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <AccessTimeIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                        {open && (
                            <Typography
                                variant="caption"
                                sx={{
                                    color: 'text.secondary',
                                    fontWeight: 'bold',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {t('common.allTimesIn', { timezone: timezoneLabel })}
                            </Typography>
                        )}
                    </div>
                </Tooltip>
            </ListItem>
        </List>
    );
}
